/**
 * Attendance per component, as the trainer and the team see it (portal PR 8b).
 *
 * The server computes everything — absences per component, the states, what
 * blocks Anexa 5. What is here turns its answer into sentences and keeps a
 * session's marks while they are being typed.
 */

export const STATE_TONES = { ok: "good", below: "late", recovery: "warn", eliminated: "neutral", abandon: "neutral" };
export const STATE_LABELS = {
  ok: "în regulă", below: "sub prag", recovery: "în recuperare", eliminated: "eliminat", abandon: "abandon",
};
export const STATUS_OPTIONS = [
  { value: "present", label: "Prezent" },
  { value: "partial", label: "Parțial" },
  { value: "absent", label: "Absent" },
];
export const COMPONENTS = [
  { value: "theoretical", label: "teoretică", short: "Teoretic" },
  { value: "practical", label: "practică", short: "Practic" },
];
export const FORMS = [
  { value: "sync", label: "online sincron" },
  { value: "async", label: "online asincron" },
];
export const JUSTIFICATIONS = [
  { value: "medical", label: "Motiv medical" },
  { value: "force_majeure", label: "Forță majoră" },
];
export const REPORT_KINDS = [
  { value: "platform", label: "Generat de platformă (Google Meet)" },
  { value: "manual", label: "Întocmit de echipă (de exemplu, foaia noastră Excel)" },
];
/** The plan-cadru's themes (backend: services/portal/curriculum.py). */
export const THEMES = [
  "Fundamentele inteligenței artificiale",
  "Comunicarea eficientă cu instrumentele AI (Prompt engineering)",
  "Gândire critică și etică în utilizarea AI",
  "Proiectarea lecțiilor și optimizarea activităților didactice cu AI",
  "Diferențierea învățării cu ajutorul AI",
  "Integrarea AI în activități specifice disciplinei",
  "Crearea de materiale vizuale cu AI",
  "Elaborarea documentelor și prezentărilor educaționale cu AI",
  "Crearea integrată de conținut educațional și pregătirea proiectului final",
  "Proiectul final: elaborare și pregătirea susținerii",
];

export const DOC_KINDS = [
  { value: "annex_4_2", label: "Anexa 4.2", title: "Raport de prezență zilnică, online sincron", perDay: true },
  { value: "annex_4_3", label: "Anexa 4.3", title: "Raport de participare, online asincron" },
  { value: "annex_5", label: "Anexa 5", title: "Catalog centralizator de prezență" },
];

/** "1,5" — Romanian decimal comma, no trailing zeros. */
export function num(x) {
  const v = Math.round(Number(x || 0) * 100) / 100;
  return String(v).replace(".", ",");
}

export function hours(x) {
  const v = Math.round(Number(x || 0) * 100) / 100;
  if (v === 1) return "1 oră";
  return `${num(v)} ore`;
}

/** "1,5 ore absențe din 6 (25%)" for one component. */
export function componentLine(c) {
  if (!c) return "";
  if (!c.absent) return `fără absențe din ${hours(c.budget)}`;
  return `${hours(c.absent)} absențe din ${hours(c.budget)} (${num(c.absent_percent)}%)`;
}

/** The session's key goes in the URL: it holds a colon. */
export function sessionPath(base, groupId, key) {
  return `${base}/groups/${groupId}/presence/${encodeURIComponent(key)}`;
}

const TZ = "Europe/Bucharest";

export function dayLabel(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, weekday: "short", day: "numeric", month: "short" })
    .format(new Date(`${iso}T12:00:00Z`));
}

export function whenLabel(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("ro-RO", {
    timeZone: TZ, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
}

/** "Confirmată", "Marcată, neconfirmată", "De marcat", "Urmează". */
export function sessionStatus(s) {
  if (s.confirmed) return { label: "Confirmată", tone: "good" };
  if (s.marked) return { label: "Marcată, neconfirmată", tone: "warn" };
  if (s.held) return { label: "De marcat", tone: "late" };
  return { label: "Urmează", tone: "neutral" };
}

/** The marks being typed, one row per participant, from what is saved. */
export function draftFrom(detail) {
  const saved = Object.fromEntries((detail?.attendance?.marks || []).map((m) => [m.enrollment_id, m]));
  return (detail?.participants || []).map((p) => {
    const m = saved[p.enrollment_id];
    return {
      enrollment_id: p.enrollment_id, name: p.name, abandon: p.abandon,
      status: m?.status || "",
      theoretical: m?.status === "partial" ? String(m.missing.theoretical || "") : "",
      practical: m?.status === "partial" ? String(m.missing.practical || "") : "",
    };
  });
}

/** What the server is sent: the rows somebody marked. */
export function marksPayload(draft) {
  return draft.filter((r) => r.status).map((r) => ({
    enrollment_id: r.enrollment_id,
    status: r.status,
    missing: r.status === "partial"
      ? { theoretical: Number(String(r.theoretical || 0).replace(",", ".")) || 0,
          practical: Number(String(r.practical || 0).replace(",", ".")) || 0 }
      : {},
  }));
}

/** A row the server would refuse, said before it is sent. */
export function rowProblem(row, session) {
  if (row.status !== "partial") return "";
  const t = Number(String(row.theoretical || 0).replace(",", ".")) || 0;
  const p = Number(String(row.practical || 0).replace(",", ".")) || 0;
  if (t < 0 || p < 0) return "Orele nu pot fi negative.";
  if (t > session.theory) return `La teoretic sesiunea are doar ${hours(session.theory)}.`;
  if (p > session.practice) return `La practic sesiunea are doar ${hours(session.practice)}.`;
  if (t + p <= 0) return "Scrie câte ore a lipsit.";
  return "";
}

export function unmarkedActive(draft) {
  return draft.filter((r) => !r.abandon && !r.status).length;
}

/** "v2 · 12 oct., 14:05 · Ion Formator". */
export function versionLine(doc) {
  const who = doc.generated_by?.name || doc.generated_by?.email || "";
  return [`v${doc.version}`, whenLabel(doc.generated_at), who].filter(Boolean).join(" · ");
}

export function asciiName(name) {
  return String(name || "grupa").normalize("NFKD").replace(/[^\w.-]+/g, "_");
}

/** "1 oct." — a day without the weekday, for a period. */
function shortDay(iso) {
  return new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, day: "numeric", month: "short" })
    .format(new Date(`${iso}T12:00:00Z`));
}

/** The label of a document's scope: a day (4.2) or a numbered period (4.3). */
export function scopeLabel(doc, periods = []) {
  if (!doc.scope) return "";
  if (doc.kind !== "annex_4_3") return dayLabel(doc.scope);
  const [start, end] = doc.scope.split("_");
  const p = periods.find((x) => x.scope === doc.scope);
  return `${p ? `Raportul ${p.number}, ` : ""}${shortDay(start)} – ${shortDay(end)}`;
}

/**
 * What a 4.3 report for [start, end] would hold (T-3): the activities whose
 * due day falls in it, their hours, and what is worth a warning. Nothing here
 * blocks; the server only refuses a period with no activity in it.
 */
export function periodPreview(annex, start, end, now = new Date()) {
  const acts = annex?.activities || [];
  if (!start || !end || start > end) return null;
  const inside = acts.filter((a) => a.due_day && a.due_day >= start && a.due_day <= end);
  const owner = {};
  (annex?.periods || []).forEach((p) => {
    if (p.start === start && p.end === end) return; // a new version of the same report
    p.activity_ids.forEach((id) => { owner[id] = owner[id] || p.number; });
  });
  return {
    activities: inside,
    hours: Math.round(inside.reduce((s, a) => s + Number(a.hours || 0), 0) * 100) / 100,
    alreadyIn: inside.filter((a) => owner[a.id]).map((a) => ({ title: a.title, number: owner[a.id] })),
    notDue: inside.filter((a) => a.due_at && new Date(a.due_at) > now).map((a) => a.title),
    noDueDate: acts.filter((a) => !a.due_day).map((a) => a.title),
  };
}
